import { IIntegration } from './../../../types/Integrations/IIntegration';
import IActionNode from '@/types/Atomic/IActions';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import useTemplateExports from './useTemplateExports';
import useLettrPromo, { LETTR_LOGO } from '@/composables/useLettrPromo';

const useExportNodes = (integrationsProp: IIntegration<unknown>[]) => {
    const getLowerCaseTypeAndCapitalizeFirst = (
        integration: IIntegration<unknown>
    ) => {
        return integration.type
            .toLowerCase()
            .replace(/_/g, ' ')
            .replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase());
    };

    /**
     * Lettr is admin-only while it is rolled out internally, so the promo is
     * only offered to someone who can actually connect it.
     *
     * It sits among the top level actions rather than inside Export: buried a
     * hover deep in a submenu nobody opens until they already know where they
     * are sending a template, it would never be seen.
     */
    const createLettrPromoNode = (): IActionNode | undefined => {
        const globalProps = usePage<ITopolPageProps>().props;
        const hasLettrIntegration = integrationsProp.some(
            (integration) => integration.type === 'LETTR'
        );

        if (!globalProps.isAdmin || hasLettrIntegration) {
            return undefined;
        }

        const { trackImpression, connect } = useLettrPromo();

        return {
            name: 'Lettr',
            promo: {
                logo: LETTR_LOGO,
                actionLabel: 'Connect',
                onShown: () => trackImpression('lettr_export_menu'),
            },
            onClick: () => connect('lettr_export_menu'),
        };
    };

    const createIntegrationsNode = (
        templateId: number,
        templateName: string
    ): IActionNode | undefined => {
        const integrations: IActionNode[] = [];

        const { exportTemplate } = useTemplateExports(templateId, templateName);

        // Lettr leads and Ecomail sits right behind it, matching the export
        // list on the editor page: both are house picks rather than something
        // the user connected.
        integrationsProp
            .filter((integration) => integration.type === 'LETTR')
            .forEach((integration: IIntegration<unknown>) => {
                integrations.push({
                    name:
                        getLowerCaseTypeAndCapitalizeFirst(integration) +
                        ' ' +
                        integration.name,
                    onClick: async () => {
                        exportTemplate(integration);
                    },
                });
            });

        integrations.push({
            name: 'Ecomail',
            onClick: () => {
                window.open(
                    `https://ecomail.app/free?appTemplateId=${templateId}`,
                    '_blank'
                );
            },
        });
        if (integrationsProp.length === 0) {
            return {
                name: 'Export',
                children: integrations,
            };
        }

        integrationsProp
            .filter((integration) => integration.type !== 'LETTR')
            .forEach((integration: IIntegration<unknown>) => {
                integrations.push({
                    name:
                        getLowerCaseTypeAndCapitalizeFirst(integration) +
                        ' ' +
                        integration.name,
                    onClick: async () => {
                        exportTemplate(integration);
                    },
                });
            });

        return {
            name: 'Export',
            children: integrations,
        };
    };

    return {
        createIntegrationsNode,
        createLettrPromoNode,
    };
};

export default useExportNodes;
